import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  ApiClient,
  ApiError,
  SYSTEM_PERMISSIONS,
  type LoginResponse,
  type ModuleMetadata,
  type RequestOptions
} from '@xjlv2/api-client'
import { normalizeTenantDomain, withResolvedTenantDomain } from './sessionDomain'
import { getStorageItem, setStorageItem, removeStorageItem } from './storage'

export { normalizeTenantDomain, withResolvedTenantDomain } from './sessionDomain'
export { getStorageItem, setStorageItem, removeStorageItem } from './storage'

export type StoredSaasSession = LoginResponse & {
  tenantDomain?: string
}

export const SAAS_SESSION_STORAGE_KEY = 'xjlv2:saas-session'

/**
 * SaaS 租户会话与权限管理 Store
 */
export const useSessionStore = defineStore('session', () => {
  const user = ref<LoginResponse | null>(null)
  const modules = ref<ModuleMetadata[]>([])

  // 单一真相来源：所有派生字段从 user 计算
  const tenantId = computed<number | null>(() => user.value?.tenantId ?? null)
  const tenantDomain = computed<string>(() => normalizeTenantDomain(user.value?.tenantDomain))
  const userId = computed<number | null>(() => user.value?.userId ?? null)
  const deptId = computed<number | null>(() => user.value?.deptId ?? null)
  const username = computed<string>(() => user.value?.username ?? '')
  const token = computed<string>(() => user.value?.token ?? '')
  const permissions = computed<string[]>(() => user.value?.permissions ?? [])
  const permissionSet = computed<Set<string>>(() => new Set(permissions.value))
  const dataScopes = computed(() => user.value?.dataScopes ?? [])
  const signedIn = computed<boolean>(() => user.value !== null)
  const canAccessWorkspace = computed<boolean>(() => hasPermission(SYSTEM_PERMISSIONS.workspaceAccess))

  /**
   * 校验是否具备指定权限代码（支持超级管理员通配符 '*'）
   */
  function hasPermission(permission: string): boolean {
    return permissionSet.value.has('*') || permissionSet.value.has(permission)
  }

  /**
   * 校验是否具备任意一个指定权限代码
   */
  function hasAnyPermission(...checkPermissions: string[]): boolean {
    if (checkPermissions.length === 0) return true
    if (permissionSet.value.has('*')) return true
    return checkPermissions.some((p) => permissionSet.value.has(p))
  }

  /**
   * 校验是否具备全部指定权限代码
   */
  function hasAllPermissions(...checkPermissions: string[]): boolean {
    if (checkPermissions.length === 0) return true
    if (permissionSet.value.has('*')) return true
    return checkPermissions.every((p) => permissionSet.value.has(p))
  }

  /**
   * 创建携带当前会话 Token 的 ApiClient 实例
   */
  function createApiClient(options: RequestOptions = {}): ApiClient {
    return new ApiClient({
      token: token.value || undefined,
      ...options
    })
  }

  /**
   * 从后端刷新当前会话信息
   */
  async function refreshSession(): Promise<void> {
    const client = createApiClient()
    const currentTenantDomain = tenantDomain.value
    try {
      const refreshedUser = await client.get<LoginResponse>('/api/auth/me')
      user.value = withResolvedTenantDomain(refreshedUser, currentTenantDomain)
      persist()
    } catch (error) {
      if (isInvalidSessionError(error)) {
        logout()
      }
      throw error
    }
  }

  /**
   * 用户名密码登录
   */
  async function login(usernameInput: string, passwordInput: string, requestedTenantDomain?: string): Promise<void> {
    const client = new ApiClient({ gateway: '/api' })
    const normalizedTenantDomain = normalizeTenantDomain(requestedTenantDomain)
    const loginResponse = await client.post<LoginResponse>('/auth/login', {
      username: usernameInput,
      password: passwordInput,
      tenantDomain: normalizedTenantDomain || undefined
    })
    user.value = withResolvedTenantDomain(loginResponse, normalizedTenantDomain)
    persist()
    await loadModules()
  }

  /**
   * 通过后端直接返回的登录响应初始化会话
   */
  async function loginFromResponse(response: LoginResponse): Promise<void> {
    user.value = withResolvedTenantDomain(response)
    persist()
    await loadModules()
  }

  /**
   * 加载当前租户已授权模块元数据列表
   */
  async function loadModules(): Promise<void> {
    if (!signedIn.value) {
      modules.value = []
      return
    }
    const client = createApiClient()
    modules.value = await client.get<ModuleMetadata[]>('/api/saas/current/modules')
  }

  /**
   * 从本地存储中恢复会话并验证有效性
   */
  async function restore(expectedTenantDomain?: string): Promise<void> {
    const raw = getStorageItem(SAAS_SESSION_STORAGE_KEY)
    if (!raw) return

    try {
      const restored = JSON.parse(raw) as StoredSaasSession
      const restoredTenantDomain = normalizeTenantDomain(restored.tenantDomain)
      const requestedTenantDomain = normalizeTenantDomain(expectedTenantDomain)
      if (requestedTenantDomain && restoredTenantDomain !== requestedTenantDomain) {
        logout()
        return
      }
      user.value = withResolvedTenantDomain(restored, restoredTenantDomain)
    } catch {
      logout()
      return
    }

    try {
      await refreshSession()
      await loadModules()
    } catch (error) {
      if (isInvalidSessionError(error)) {
        if (signedIn.value) {
          logout()
        }
        return
      }
      throw error
    }
  }

  /**
   * 判定错误是否为会话失效或未认证异常
   */
  function isInvalidSessionError(error: unknown): boolean {
    return error instanceof ApiError && (
      error.status === 401 ||
      error.status === 403 ||
      error.code === 'UNAUTHENTICATED' ||
      error.code === 'TOKEN_EXPIRED' ||
      error.code === 'TENANT_SUSPENDED'
    )
  }

  /**
   * 退出登录并清理本地会话
   */
  function logout(): void {
    user.value = null
    modules.value = []
    removeStorageItem(SAAS_SESSION_STORAGE_KEY)
  }

  /**
   * 持久化会话到本地存储
   */
  function persist(): void {
    if (!user.value) return
    setStorageItem(SAAS_SESSION_STORAGE_KEY, JSON.stringify(user.value))
  }

  return {
    tenantId,
    tenantDomain,
    userId,
    deptId,
    username,
    token,
    user,
    modules,
    permissions,
    permissionSet,
    dataScopes,
    signedIn,
    canAccessWorkspace,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    createApiClient,
    refreshSession,
    login,
    loginFromResponse,
    loadModules,
    restore,
    logout
  }
})
